import { Component, inject, Input } from '@angular/core';
import { AsyncPipe, LowerCasePipe, NgFor, NgIf, NgStyle } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { common } from '@qwyk/models';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { CustomTagsFacade, SharedStoresCustomTagsModule } from '@qwyk/shared-stores/custom-tags';

import {
    AutoComplete,
    AutoCompleteModule,
    AutoCompleteSelectEvent,
    AutoCompleteCompleteEvent,
} from 'primeng/autocomplete';
import { map } from 'rxjs/operators';
import { SharedModule } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { ColorPickerModule } from 'primeng/colorpicker';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ReactiveValidationModule, Validators } from 'angular-reactive-validation';

@Component({
    standalone: true,
    imports: [
        NgIf,
        NgFor,
        NgStyle,
        AsyncPipe,
        FormsModule,
        DialogModule,
        SharedModule,
        LowerCasePipe,
        TooltipModule,
        TranslateModule,
        InputTextModule,
        ColorPickerModule,
        FontAwesomeModule,
        AutoCompleteModule,
        OverlayPanelModule,
        ReactiveFormsModule,
        ReactiveValidationModule,
        SharedStoresCustomTagsModule,
    ],
    selector: 'qwyk-tags-list',
    templateUrl: './tags-list.component.html',
    styleUrls: ['./tags-list.component.scss'],
    providers: [CustomTagsFacade],
})
export class TagsListComponent {
    private readonly _fb = inject(FormBuilder);
    private readonly _tags = inject(CustomTagsFacade);
    private readonly _translate = inject(TranslateService);

    @Input() tags: common.CustomTag[] = [];
    @Input() entityId: string;
    @Input() type: 'shipment' | 'warehouse-receipt' | 'cargo-detail' | 'cargo-release' | 'pickup-order' | 'purchase-order';

    showAutocomplete = false;
    selectedTags: common.CustomTag[] = [];
    showModal = false;
    isEditingMode = false;
    tagId: string;

    tagForm: FormGroup = this._fb.group({
        'name': [null, [Validators.required()]],
        'color': ['#000000', [Validators.required()]],
    });

    loadingCustomTags$ = this._tags.loading$;
    suggestions$ = this._tags.suggestions$.pipe(map(suggestions => {
        return suggestions.map(suggestion => {
            const name = suggestion.query ? `${this._translate.instant('common.add-item', { value: suggestion.query })}...` : suggestion.name;
            return { ...suggestion, name };
        });
    }));

    public trackByIndex(index: number, item: any): number {
        return index;
    }

    public onSelectTag(event: AutoCompleteSelectEvent) {
        if (event.value.query) {
            this.selectedTags = event.value.query;
            this.onAddItem();
            this.selectedTags = [];
            return;
        }

        const customTag = {
            type: 'tag',
            attributes: {
                type: this.type,
                type_id: [this.entityId],
                upsert: false,
                name: event.value.name
            },
        };
        const request = {
            data: [customTag],
            tagId: `${event.value.id}`,
        };
        this.selectedTags = [];
        this.showAutocomplete = false;

        return this._tags.linkTagToEntity(request);
    }

    public onAddItem(): void {
        this.showModal = !this.showModal;
        this.tagForm.patchValue({ name: this.selectedTags });
    }

    public onSearchTags(event: AutoCompleteCompleteEvent) {
        if (event.query !== '') {
            this._tags.searchTags(event.query);
        } else {
            this._tags.loadTags({ page: 1, pageSize: 100, portalOnly: 1 }, true);
        }
    }

    public onEditTag(tag: common.CustomTag) {
        this.tagId = tag.id;
        this.isEditingMode = true;
        this.tagForm.patchValue({
            name: tag.attributes.name,
            color: tag.attributes.color
        });
        this.showModal = true;
    }

    public onRemoveTag(tagId: string) {
        return this._tags.unlinkTagFromEntity({ tagId, entityId: this.entityId, entity: this.type });
    }

    public onSubmit() {
        this.showModal = false;
        const formValues = this.tagForm.value;
        this.selectedTags = [];

        const commonPayloadAttributes = {
            type: this.type,
            color: formValues.color,
            is_system_tag: false,
        };

        if (this.isEditingMode) {
            const updateAttributes = {
                ...commonPayloadAttributes,
                name: formValues.name,
                upsert: false
            };

            const customTagPayload: Partial<common.CustomTag> = {
                type: 'tag',
                id: this.tagId,
                attributes: updateAttributes,
            };

            return this._tags.updateTag(customTagPayload, this.entityId);

        } else {
            const createAttributes = {
                ...commonPayloadAttributes,
                name: formValues.name,
                type_id: [this.entityId],
            };

            const customTagPayload: Partial<common.CustomTag> = {
                type: 'tag',
                attributes: createAttributes,
            };

            const request = {
                data: [customTagPayload],
            };
            return this._tags.createTagAndLinkToEntity(request);
        }
    }

    public onSearchAll(autocomplete: AutoComplete, e: Event): void {
        if (autocomplete) {
            autocomplete.show();
        }

        const p: AutoCompleteCompleteEvent = {
            originalEvent: e,
            query: ''
        }

        this.onSearchTags(p);
    }
}
